/**
 * Dashboard Overview Component
 * Main landing page with KPIs, stats, and quick actions
 */

import React, { useState, useEffect } from 'react';
import DataSourceAttribution from '../common/DataSourceAttribution';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Psychology as AIIcon,
  CloudQueue as CloudIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import { subDays } from 'date-fns';
import { prefetchOverview, peekOverview, isOverviewStale, overviewOwner } from '../../services/overviewPrefetch';
import MetricCard from '../common/MetricCard';
import { colors, spacing } from '../../theme/designTokens';

interface DashboardOverview {
  today: {
    revenue: number;
    transactions: number;
    unique_customers: number;
  };
  totals: {
    lifetime_revenue: number;
    total_transactions: number;
    total_customers: number;
  };
  subscriptions: {
    total_subscriptions: number;
    active_subscriptions: number;
    current_mrr: number;
  };
  month: {
    revenue: number;
    transactions: number;
    customers: number;
    revenue_growth_rate: number;
    customer_growth_rate: number;
  };
  content: {
    total_chapters: number;
    total_users: number;
    chapters_last_30_days: number;
    users_last_30_days: number;
    chapter_authors: number;
  };
  comparison: {
    revenue_change: number;
    customer_change: number;
    subscriber_change: number;
  };
}

interface SystemHealth {
  status: string;
  uptime: number;
  memory_usage: number;
  cpu_usage: number;
  database_status: string;
  active_connections: number;
}

interface CommunicationStats {
  email_campaigns_sent: number;
  sms_sent: number;
  push_notifications_sent: number;
  email_open_rate: number;
  sms_delivery_rate: number;
  push_engagement_rate: number;
}

interface AIQueueStats {
  totalTasks: number;
  activeTasks: number;
  completedTasks: number;
  failedTasks: number;
  totalCost: number;
  dailyCost: number;
  grokTokensUsed: number;
  averageTaskCost: number;
  costByType: {
    textGeneration: number;
    imageGeneration: number;
    styleApplication: number;
    moodEnhancement: number;
  };
  costByProduct: {
    unlimited_subscription: number;
    chapter_credits: number;
    individual_purchase: number;
  };
}

interface RecentActivity {
  id: string;
  type: 'user_added' | 'email_sent' | 'system_alert' | 'revenue_milestone';
  title: string;
  description: string;
  timestamp: string;
  user?: string;
  severity?: 'low' | 'medium' | 'high';
}

// Enhanced Business Intelligence Metrics Interfaces
interface EnhancedDashboardResponse {
  overview: DashboardOverview;
  userMetrics: UserMetrics | null;
  revenueMetrics: RevenueMetrics | null;
  aiCostMetrics: AICostMetrics | null;
  chartData: ChartData | null;
  partialData: boolean;
  queryTime: number;
  timestamp: string;
}

interface UserMetrics {
  totalUsers: MetricCard;
  activeUsers: MetricCard;
  newUsersToday: MetricCard;
  activeSubscriptions: MetricCard;
}

interface RevenueMetrics {
  todayRevenue: MetricCard;
  mtdRevenue: MetricCard;
  subscriptionRevenue: MetricCard;
  ytdRevenue: MetricCard;
}

interface AICostMetrics {
  dailyAICosts: CostBreakdown;
  monthlyAICosts: CostBreakdown;
  mtdTotalCosts: MetricCard;
  ytdAICosts: MetricCard;
}

interface MetricCard {
  value: string | number;
  label: string;
  icon: string;
  trend?: string;
  trendDirection?: 'up' | 'down';
  subtitle?: string;
  rawValue?: number;
}

interface CostBreakdown {
  textCost: string;
  imageCost: string;
  totalDaily?: string;
  totalMonthly?: string;
  label: string;
  icon: string;
}

interface ChartData {
  revenueTimeline: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      borderColor: string;
      backgroundColor: string;
      fill?: boolean;
    }>;
  };
}

// Enhanced Analytics Interfaces
interface EnhancedRevenueAnalytics {
  todayTotal: MetricCardData;
  todayNew: MetricCardData;
  mtdRevenue: MetricCardData;
  lastMonthRevenue: MetricCardData;
  ytdRevenue: MetricCardData;
}

interface EnhancedAICostAnalytics {
  dailyAICosts: MetricCardData;
  mtdAICosts: MetricCardData;
  lastMonthAICosts: MetricCardData;
  ytdAICosts: MetricCardData;
}

interface GrossMarginAnalytics {
  mtdMargin: MarginData;
  lastMonthMargin: MarginData;
  ytdMargin: MarginData;
  projectedAnnualMargin: MarginData;
}

interface EnhancedAWSCostAnalytics {
  dailyAWSCosts: MetricCardData;
  mtdAWSCosts: MetricCardData;
  lastMonthAWSCosts: MetricCardData;
  ytdAWSCosts: MetricCardData;
}

interface MetricCardData {
  value: string;
  rawValue: number;
  label: string;
  subtitle?: string;
  change?: string;
}

interface MarginData {
  value: string;
  rawValue: number;
  revenue: string;
  costs: string;
  health: string;
  label: string;
  subtitle: string;
  change?: string;
}


export const DashboardOverview: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [communicationStats, setCommunicationStats] = useState<CommunicationStats | null>(null);
  const [aiQueueStats, setAIQueueStats] = useState<AIQueueStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Enhanced Business Intelligence Metrics State
  const [enhancedData, setEnhancedData] = useState<EnhancedDashboardResponse | null>(null);
  
  // Enhanced Analytics State
  const [enhancedRevenue, setEnhancedRevenue] = useState<EnhancedRevenueAnalytics | null>(null);
  const [enhancedAICosts, setEnhancedAICosts] = useState<EnhancedAICostAnalytics | null>(null);
  const [enhancedAWSCosts, setEnhancedAWSCosts] = useState<EnhancedAWSCostAnalytics | null>(null);
  const [grossMargins, setGrossMargins] = useState<GrossMarginAnalytics | null>(null);

  // Subscription Intelligence — authoritative active subscriber counts
  const [intelligenceHealth, setIntelligenceHealth] = useState<{
    totalNewSinceCutoff: number;
    monthlySinceCutoff: number;
    annualSinceCutoff: number;
  } | null>(null);


  /**
   * The nine Overview endpoints come from services/overviewPrefetch, which Layout
   * starts at sign-in: a cached or in-flight result is reused rather than
   * refetched, so the page no longer waits ~30 s on every visit. `force`
   * refetches (Refresh); `quiet` suppresses the load toasts for a silent
   * background refresh.
   */
  const loadDashboardData = async ({ force = false, quiet = false }: { force?: boolean; quiet?: boolean } = {}) => {
    const notify = (n: Parameters<typeof addNotification>[0]) => { if (!quiet) dispatch(addNotification(n)); };
    try {
      console.log('Loading dashboard data...');

      // Load dashboard overview data with individual error handling including enhanced analytics
      const [
        overviewResponse,
        healthResponse,
        commStatsResponse,
        aiQueueResponse,
        activityResponse,
        enhancedRevenueResponse,
        grossMarginsResponse,
        awsCostsResponse,
        intelligenceResponse,
      ] = (await prefetchOverview(overviewOwner(user), { force })) as PromiseSettledResult<any>[];

      let loadedSections = 0;
      let totalSections = 9;

      // Handle enhanced dashboard overview with business intelligence metrics
      if (overviewResponse.status === 'fulfilled' && overviewResponse.value) {
        const enhancedResponse = overviewResponse.value as EnhancedDashboardResponse;
        
        if (enhancedResponse && typeof enhancedResponse === 'object') {
          // Set the enhanced data state
          setEnhancedData(enhancedResponse);
          
          // Set legacy overview for backward compatibility
          setOverview(enhancedResponse.overview || enhancedResponse.legacy);
          
          loadedSections++;
          console.log('✅ Enhanced dashboard loaded successfully', enhancedResponse);
          
          // Show partial data notification if applicable
          if (enhancedResponse.partialData) {
            notify({
              type: 'warning',
              message: 'Dashboard loaded with partial data - some metrics may be incomplete',
            });
          }
        } else {
          console.warn('⚠️ Dashboard overview failed: No overview data found in response');
          throw new Error('No overview data in response');
        }
      } else {
        console.warn('⚠️ Dashboard overview failed:', overviewResponse.status === 'rejected' ? overviewResponse.reason : 'Invalid response structure');
        // Set fallback overview data
        setOverview({
          today: { revenue: 2400, transactions: 45, unique_customers: 38 },
          totals: { lifetime_revenue: 156800, total_transactions: 2340, total_customers: 1892 },
          subscriptions: { total_subscriptions: 456, active_subscriptions: 423, current_mrr: 8950 },
          month: { revenue: 24500, transactions: 890, customers: 234, revenue_growth_rate: 12.5, customer_growth_rate: 8.2 },
          content: { total_chapters: 1250, total_users: 892, chapters_last_30_days: 145, users_last_30_days: 67, chapter_authors: 234 },
          comparison: { revenue_change: 15.8, customer_change: 8.2, subscriber_change: 6.7 }
        });
        setEnhancedData(null);
      }

      // Handle system health with fallback
      if (healthResponse.status === 'fulfilled' && healthResponse.value) {
        setSystemHealth(healthResponse.value);
        loadedSections++;
        console.log('✅ System health loaded successfully');
      } else {
        console.warn('⚠️ System health failed:', healthResponse.status === 'rejected' ? healthResponse.reason : 'No data');
        setSystemHealth({
          status: 'operational',
          uptime: 99.5,
          memory_usage: 65,
          cpu_usage: 45,
          database_status: 'connected',
          active_connections: 150
        });
      }

      // Handle communication stats with fallback
      if (commStatsResponse.status === 'fulfilled' && commStatsResponse.value) {
        setCommunicationStats(commStatsResponse.value);
        loadedSections++;
        console.log('✅ Communication stats loaded successfully');
      } else {
        console.warn('⚠️ Communication stats failed:', commStatsResponse.status === 'rejected' ? commStatsResponse.reason : 'No data');
        setCommunicationStats({
          email_campaigns_sent: 0,
          sms_sent: 0,
          push_notifications_sent: 0,
          email_open_rate: 0,
          sms_delivery_rate: 0,
          push_engagement_rate: 0
        });
      }

      // Handle AI Queue stats from integrated AI & AWS monitor with fallback
      if (aiQueueResponse.status === 'fulfilled' && aiQueueResponse.value) {
        // Extract AI queue metrics from integrated monitor response
        const integratedData = aiQueueResponse.value;
        const aiQueueMetrics = integratedData.metricCards?.aiQueue || integratedData.data?.metricCards?.aiQueue || {};
        
        // Map integrated monitor data to expected AI queue format
        const mappedAIQueueStats = {
          totalTasks: aiQueueMetrics.totalTasks || 0,
          activeTasks: aiQueueMetrics.processingTasks || 0,
          completedTasks: aiQueueMetrics.completedTasks || 0,
          failedTasks: aiQueueMetrics.failedTasks || 0,
          totalCost: aiQueueMetrics.totalCost || 0,
          dailyCost: integratedData.enhancedCosts?.dailyAICosts?.rawValue || 0,
          grokTokensUsed: 125000, // Mock until we have real token tracking
          averageTaskCost: aiQueueMetrics.totalTasks > 0 ? (aiQueueMetrics.totalCost / aiQueueMetrics.totalTasks) : 0,
          costByType: {
            textGeneration: aiQueueMetrics.totalCost * 0.5 || 0,
            imageGeneration: aiQueueMetrics.totalCost * 0.3 || 0,
            styleApplication: aiQueueMetrics.totalCost * 0.15 || 0,
            moodEnhancement: aiQueueMetrics.totalCost * 0.05 || 0
          },
          costByProduct: {
            unlimited_subscription: aiQueueMetrics.totalCost * 0.7 || 0,
            chapter_credits: aiQueueMetrics.totalCost * 0.2 || 0,
            individual_purchase: aiQueueMetrics.totalCost * 0.1 || 0
          }
        };
        
        setAIQueueStats(mappedAIQueueStats);
        loadedSections++;
        console.log('✅ AI Queue stats loaded from integrated monitor successfully');
      } else {
        console.warn('⚠️ AI Queue stats failed:', aiQueueResponse.status === 'rejected' ? aiQueueResponse.reason : 'No data');
        setAIQueueStats({
          totalTasks: 150,
          activeTasks: 12,
          completedTasks: 138,
          failedTasks: 2,
          totalCost: 245.67,
          dailyCost: 18.45,
          grokTokensUsed: 125000,
          averageTaskCost: 1.64,
          costByType: {
            textGeneration: 120.45,
            imageGeneration: 89.22,
            styleApplication: 25.67,
            moodEnhancement: 10.33
          },
          costByProduct: {
            unlimited_subscription: 180.45,
            chapter_credits: 45.22,
            individual_purchase: 20.00
          }
        });
      }

      // Handle recent activity with fallback
      if (activityResponse.status === 'fulfilled' && activityResponse.value) {
        // Check if the response has activities array or is direct array
        const activities = Array.isArray(activityResponse.value) 
          ? activityResponse.value 
          : (activityResponse.value?.data?.activities || activityResponse.value?.activities || []);
        
        setRecentActivity(activities);
        loadedSections++;
        console.log('✅ Recent activity loaded successfully');
      } else {
        console.warn('⚠️ Recent activity failed:', activityResponse.status === 'rejected' ? activityResponse.reason : 'Invalid data');
        // Fallback to sample activity data
        const sampleActivity: RecentActivity[] = [
          {
            id: '1',
            type: 'user_added',
            title: 'Admin dashboard accessed',
            description: 'Dashboard components loaded successfully',
            timestamp: new Date().toISOString(),
            user: user?.name || 'Admin User',
          },
          {
            id: '2',
            type: 'system_alert',
            title: 'System status check',
            description: 'Dashboard functionality verified',
            timestamp: subDays(new Date(), 1).toISOString(),
            severity: 'low',
          }
        ];
        setRecentActivity(sampleActivity);
      }

      // Handle Enhanced Revenue Analytics
      // V5.4.37: Extract enhancedRevenue from the nested response structure
      if (enhancedRevenueResponse.status === 'fulfilled' && enhancedRevenueResponse.value) {
        // The API returns { overview: {...}, enhancedRevenue: {...}, last_updated: ... }
        const responseData = enhancedRevenueResponse.value;
        if (responseData.enhancedRevenue) {
          setEnhancedRevenue(responseData.enhancedRevenue);
          console.log('✅ Enhanced revenue analytics loaded successfully', responseData.enhancedRevenue);
        } else {
          // Fallback: try to use the response directly if it has the expected structure
          setEnhancedRevenue(responseData);
          console.log('✅ Enhanced revenue analytics loaded (direct structure)');
        }
        loadedSections++;
      } else {
        console.warn('⚠️ Enhanced revenue analytics failed:', enhancedRevenueResponse.status === 'rejected' ? enhancedRevenueResponse.reason : 'No data');
        setEnhancedRevenue(null);
      }

      // Handle Enhanced AI Cost Analytics (extracted from integrated AI & AWS monitor)
      if (aiQueueResponse.status === 'fulfilled' && aiQueueResponse.value?.enhancedCosts) {
        const enhancedCosts = aiQueueResponse.value.enhancedCosts;
        setEnhancedAICosts({
          dailyAICosts: enhancedCosts.dailyAICosts,
          mtdAICosts: enhancedCosts.mtdAICosts,
          lastMonthAICosts: enhancedCosts.lastMonthAICosts,
          ytdAICosts: enhancedCosts.ytdAICosts
        });
        loadedSections++;
        console.log('✅ Enhanced AI cost analytics loaded from integrated monitor successfully');
        console.log('🔍 AI Cost Trend Data Debug:', {
          mtdAICosts: enhancedCosts.mtdAICosts,
          hasChange: !!enhancedCosts.mtdAICosts?.change,
          changeValue: enhancedCosts.mtdAICosts?.change,
          changeType: typeof enhancedCosts.mtdAICosts?.change
        });
      } else {
        console.warn('⚠️ Enhanced AI cost analytics failed or no enhanced cost data in integrated monitor response');
        console.log('🔍 Integrated AI monitor response debug:', aiQueueResponse);
        setEnhancedAICosts(null);
      }

      // Handle Gross Margin Analytics
      if (grossMarginsResponse.status === 'fulfilled' && grossMarginsResponse.value) {
        setGrossMargins(grossMarginsResponse.value);
        loadedSections++;
        console.log('✅ Gross margin analytics loaded successfully');
      } else {
        console.warn('⚠️ Gross margin analytics failed:', grossMarginsResponse.status === 'rejected' ? grossMarginsResponse.reason : 'No data');
        setGrossMargins(null);
      }

      // Handle AWS Cost Analytics
      if (awsCostsResponse.status === 'fulfilled' && awsCostsResponse.value) {
        setEnhancedAWSCosts(awsCostsResponse.value);
        loadedSections++;
        console.log('✅ AWS cost analytics loaded successfully');
      } else {
        console.warn('⚠️ AWS cost analytics failed:', awsCostsResponse.status === 'rejected' ? awsCostsResponse.reason : 'No data');
        setEnhancedAWSCosts(null);
      }

      // Handle Subscription Intelligence — authoritative active subscriber counts
      if (intelligenceResponse.status === 'fulfilled' && intelligenceResponse.value) {
        const health = (intelligenceResponse.value as any)?.data?.health;
        if (health) {
          setIntelligenceHealth({
            totalNewSinceCutoff: health.totalNewSinceCutoff ?? 0,
            monthlySinceCutoff: health.monthlySinceCutoff ?? 0,
            annualSinceCutoff: health.annualSinceCutoff ?? 0,
          });
          loadedSections++;
        }
      }

      // Show success message only if some data loaded
      if (loadedSections > 0) {
        console.log(`✅ Dashboard loaded: ${loadedSections}/${totalSections} sections successful`);
        if (loadedSections < totalSections) {
          notify({
            type: 'warning',
            message: `Dashboard loaded with ${totalSections - loadedSections} section(s) using fallback data`,
          });
        }
      } else {
        notify({
          type: 'error',
          message: 'Dashboard loaded with fallback data only',
        });
      }

    } catch (error) {
      console.error('❌ Dashboard data loading error:', error);
      notify({
        type: 'error',
        message: 'Failed to load dashboard data - using fallback information',
      });
      
      // Set all fallback data
      setOverview({
        today: { revenue: 0, transactions: 0, unique_customers: 0 },
        totals: { lifetime_revenue: 0, total_transactions: 0, total_customers: 0 },
        subscriptions: { total_subscriptions: 0, active_subscriptions: 0, current_mrr: 0 },
        month: { revenue: 0, transactions: 0, customers: 0, revenue_growth_rate: 0, customer_growth_rate: 0 },
        content: { total_chapters: 0, total_users: 0, chapters_last_30_days: 0, users_last_30_days: 0, chapter_authors: 0 },
        comparison: { revenue_change: 0, customer_change: 0, subscriber_change: 0 }
      });
      
      setSystemHealth({
        status: 'unknown',
        uptime: 0,
        memory_usage: 0,
        cpu_usage: 0,
        database_status: 'unknown',
        active_connections: 0
      });
      
      setCommunicationStats({
        email_campaigns_sent: 0,
        sms_sent: 0,
        push_notifications_sent: 0,
        email_open_rate: 0,
        sms_delivery_rate: 0,
        push_engagement_rate: 0
      });

      setAIQueueStats({
        totalTasks: 0,
        activeTasks: 0,
        completedTasks: 0,
        failedTasks: 0,
        totalCost: 0,
        dailyCost: 0,
        grokTokensUsed: 0,
        averageTaskCost: 0,
        costByType: {
          textGeneration: 0,
          imageGeneration: 0,
          styleApplication: 0,
          moodEnhancement: 0
        },
        costByProduct: {
          unlimited_subscription: 0,
          chapter_credits: 0,
          individual_purchase: 0
        }
      });
      
      setRecentActivity([{
        id: '1',
        type: 'system_alert',
        title: 'Dashboard in fallback mode',
        description: 'Some data may be limited or unavailable',
        timestamp: new Date().toISOString(),
        severity: 'medium',
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData({ force: true });
    setRefreshing(false);
    dispatch(addNotification({
      type: 'success',
      message: 'Dashboard data refreshed',
    }));
  };

  useEffect(() => {
    // Usually already fetched by Layout at sign-in, so this resolves at once.
    // A result older than five minutes is shown straight away and refreshed
    // quietly behind it, rather than making anyone wait for the full reload.
    const hit = peekOverview(overviewOwner(user));
    loadDashboardData().then(() => {
      if (hit && isOverviewStale(hit.at)) loadDashboardData({ force: true, quiet: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewOwner(user)]);


  // Helper function to get trend color
  const getTrendColor = (change: string | number) => {
    const numericChange = typeof change === 'string' ? parseFloat(change) : change;
    return numericChange > 0 ? theme.palette.success.main : 
           numericChange < 0 ? theme.palette.error.main : 
           theme.palette.text.secondary;
  };

  // Helper function to safely parse trend values
  const parseTrend = (trendValue?: string | number): number => {
    if (!trendValue) return 0;
    if (typeof trendValue === 'number') return trendValue;
    
    // Handle string values - remove % if present and parse
    const cleanValue = typeof trendValue === 'string' ? trendValue.replace('%', '') : String(trendValue);
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Helper function to get margin health color
  const getMarginHealthColor = (health: string) => {
    switch (health) {
      case 'excellent': return theme.palette.success.main;
      case 'good': return theme.palette.info.main;
      case 'fair': return theme.palette.warning.main;
      case 'poor': return theme.palette.orange?.main || theme.palette.warning.main;
      case 'critical': return theme.palette.error.main;
      default: return theme.palette.text.secondary;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width="300px" height={40} />
          <Skeleton variant="text" width="400px" height={24} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Skeleton variant="rectangular" height={140} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: spacing[6], pr: spacing[6], pb: spacing[6], pl: spacing[6], bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: spacing[8] }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: spacing[2] }}>
          <Typography variant="h1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: spacing[3] }}>
            <AssessmentIcon color="primary" sx={{ fontSize: 40 }} />
            Overview
          </Typography>
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            <RefreshIcon sx={{ transform: refreshing ? 'rotate(360deg)' : 'none', transition: 'transform 1s' }} />
          </IconButton>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.125rem' }}>
          Welcome back, {user?.name}! Here's what's happening with LifeScribe today.
        </Typography>
      </Box>

      {/* Row 1: User Analytics */}
      <Typography variant="h3" sx={{ fontWeight: 600, mb: spacing[4], display: 'flex', alignItems: 'center', gap: spacing[2] }}>
        <PeopleIcon color="primary" sx={{ fontSize: 32 }} />
        User Analytics
      </Typography>
      <Grid container spacing={3} sx={{ mb: spacing[6] }} alignItems="stretch">
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title="New Users Today"
            value={enhancedData?.metrics?.userMetrics?.newUsersToday?.value || 0}
            subtitle="Today"
            change={parseTrend(enhancedData?.metrics?.userMetrics?.newUsersToday?.trend)}
            trend={parseTrend(enhancedData?.metrics?.userMetrics?.newUsersToday?.trend) > 0 ? 'up' : parseTrend(enhancedData?.metrics?.userMetrics?.newUsersToday?.trend) < 0 ? 'down' : 'flat'}
            icon="user"
            color="users"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title="Active Users"
            value={enhancedData?.metrics?.userMetrics?.activeUsers?.value || overview?.content?.chapter_authors || 0}
            subtitle="Content creators"
            icon="user"
            color="users"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title="Total Users"
            value={enhancedData?.metrics?.userMetrics?.totalUsers?.value || overview?.content?.total_users || 0}
            subtitle={`${enhancedData?.metrics?.userMetrics?.activeUsers?.value || overview?.content?.chapter_authors || 0} active authors`}
            change={parseTrend(enhancedData?.metrics?.userMetrics?.totalUsers?.trend) ||
              (overview?.content?.users_last_30_days ?
                ((overview.content.users_last_30_days / Math.max(overview.content.total_users - overview.content.users_last_30_days, 1)) * 100) : 0)
            }
            trend={parseTrend(enhancedData?.metrics?.userMetrics?.totalUsers?.trend) > 0 ? 'up' : 'flat'}
            icon="users"
            color="users"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title="Active Subscriptions"
            value={
              intelligenceHealth?.totalNewSinceCutoff ??
              enhancedData?.metrics?.userMetrics?.activeSubscriptions?.value ??
              overview?.subscriptions?.active_subscriptions ??
              0
            }
            subtitle={
              intelligenceHealth
                ? `${intelligenceHealth.monthlySinceCutoff} Monthly / ${intelligenceHealth.annualSinceCutoff} Annual`
                : 'Monthly + Annual + Lifetime'
            }
            icon="subscriptions"
            color="revenue"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Row 2: Revenue Analytics */}
      <Typography variant="h3" sx={{ fontWeight: 600, mb: spacing[4], display: 'flex', alignItems: 'center', gap: spacing[2] }}>
        <MoneyIcon color="primary" sx={{ fontSize: 32 }} />
        Revenue Analytics
      </Typography>
      <Grid container spacing={3} sx={{ mb: spacing[6] }} alignItems="stretch">
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title={enhancedRevenue?.todayTotal?.label || "Today's Total Revenue"}
            value={enhancedRevenue?.todayTotal?.value || '$0.00'}
            subtitle={enhancedRevenue?.todayTotal?.subtitle || '0 transactions'}
            change={parseTrend(enhancedRevenue?.todayTotal?.change)}
            trend={parseTrend(enhancedRevenue?.todayTotal?.change) > 0 ? 'up' : parseTrend(enhancedRevenue?.todayTotal?.change) < 0 ? 'down' : 'flat'}
            icon="revenue"
            color="revenue"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title={enhancedRevenue?.mtdRevenue?.label || "MTD Revenue"}
            value={enhancedRevenue?.mtdRevenue?.value || '$0.00'}
            subtitle={enhancedRevenue?.mtdRevenue?.subtitle || 'Month to date'}
            change={parseTrend(enhancedRevenue?.mtdRevenue?.change)}
            trend={parseTrend(enhancedRevenue?.mtdRevenue?.change) > 0 ? 'up' : parseTrend(enhancedRevenue?.mtdRevenue?.change) < 0 ? 'down' : 'flat'}
            icon="revenue"
            color="revenue"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title={enhancedRevenue?.lastMonthRevenue?.label || "Last Month Revenue"}
            value={enhancedRevenue?.lastMonthRevenue?.value || '$0.00'}
            subtitle={enhancedRevenue?.lastMonthRevenue?.subtitle || 'Previous month'}
            icon="revenue"
            color="revenue"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title={enhancedRevenue?.ytdRevenue?.label || "YTD Revenue"}
            value={enhancedRevenue?.ytdRevenue?.value || '$0.00'}
            subtitle={enhancedRevenue?.ytdRevenue?.subtitle || 'Year to date breakdown'}
            change={parseTrend(enhancedRevenue?.ytdRevenue?.change)}
            trend={parseTrend(enhancedRevenue?.ytdRevenue?.change) > 0 ? 'up' : parseTrend(enhancedRevenue?.ytdRevenue?.change) < 0 ? 'down' : 'flat'}
            icon="trending_up"
            color="revenue"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Row 3: Grok AI Costs */}
      <Typography variant="h3" sx={{ fontWeight: 600, mb: spacing[4], display: 'flex', alignItems: 'center', gap: spacing[2] }}>
        <AIIcon color="primary" sx={{ fontSize: 32 }} />
        Grok AI Costs
      </Typography>
      <Grid container spacing={3} sx={{ mb: spacing[6] }} alignItems="stretch">
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title="Daily Grok Costs"
            value={enhancedAICosts?.dailyAICosts?.value || '$0.00'}
            subtitle="Grok AI costs today"
            change={parseTrend(enhancedAICosts?.dailyAICosts?.change)}
            trend={parseTrend(enhancedAICosts?.dailyAICosts?.change) > 0 ? 'up' : parseTrend(enhancedAICosts?.dailyAICosts?.change) < 0 ? 'down' : 'flat'}
            icon="ai"
            color="costs"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title="MTD Grok Costs"
            value={enhancedAICosts?.mtdAICosts?.value || '$0.00'}
            subtitle="Month to date Grok costs"
            change={parseTrend(enhancedAICosts?.mtdAICosts?.change)}
            trend={parseTrend(enhancedAICosts?.mtdAICosts?.change) > 0 ? 'up' : parseTrend(enhancedAICosts?.mtdAICosts?.change) < 0 ? 'down' : 'flat'}
            icon="ai"
            color="costs"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title="Last Month Grok Costs"
            value={enhancedAICosts?.lastMonthAICosts?.value || '$0.00'}
            subtitle="Previous month Grok costs"
            change={parseTrend(enhancedAICosts?.lastMonthAICosts?.change)}
            trend={parseTrend(enhancedAICosts?.lastMonthAICosts?.change) > 0 ? 'up' : parseTrend(enhancedAICosts?.lastMonthAICosts?.change) < 0 ? 'down' : 'flat'}
            icon="ai"
            color="costs"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title="YTD Grok Costs"
            value={enhancedAICosts?.ytdAICosts?.value || '$0.00'}
            subtitle="Year to date Grok costs"
            change={parseTrend(enhancedAICosts?.ytdAICosts?.change)}
            trend={parseTrend(enhancedAICosts?.ytdAICosts?.change) > 0 ? 'up' : parseTrend(enhancedAICosts?.ytdAICosts?.change) < 0 ? 'down' : 'flat'}
            icon="ai"
            color="costs"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Row 4: AWS Costs */}
      <Typography variant="h3" sx={{ fontWeight: 600, mb: spacing[4], display: 'flex', alignItems: 'center', gap: spacing[2] }}>
        <CloudIcon color="primary" sx={{ fontSize: 32 }} />
        AWS Costs
      </Typography>
      <Grid container spacing={3} sx={{ mb: spacing[6] }} alignItems="stretch">
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title="Daily AWS Costs"
            value={enhancedAWSCosts?.dailyAWSCosts?.value || '$0.00'}
            subtitle="AWS costs today"
            change={parseTrend(enhancedAWSCosts?.dailyAWSCosts?.change)}
            trend={parseTrend(enhancedAWSCosts?.dailyAWSCosts?.change) > 0 ? 'up' : parseTrend(enhancedAWSCosts?.dailyAWSCosts?.change) < 0 ? 'down' : 'flat'}
            icon="cloud"
            color="costs"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title="MTD AWS Costs"
            value={enhancedAWSCosts?.mtdAWSCosts?.value || '$0.00'}
            subtitle="Month to date AWS costs"
            change={parseTrend(enhancedAWSCosts?.mtdAWSCosts?.change)}
            trend={parseTrend(enhancedAWSCosts?.mtdAWSCosts?.change) > 0 ? 'up' : parseTrend(enhancedAWSCosts?.mtdAWSCosts?.change) < 0 ? 'down' : 'flat'}
            icon="cloud"
            color="costs"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title="Last Month AWS Costs"
            value={enhancedAWSCosts?.lastMonthAWSCosts?.value || '$0.00'}
            subtitle="Previous month AWS costs"
            change={parseTrend(enhancedAWSCosts?.lastMonthAWSCosts?.change)}
            trend={parseTrend(enhancedAWSCosts?.lastMonthAWSCosts?.change) > 0 ? 'up' : parseTrend(enhancedAWSCosts?.lastMonthAWSCosts?.change) < 0 ? 'down' : 'flat'}
            icon="cloud"
            color="costs"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title="YTD AWS Costs"
            value={enhancedAWSCosts?.ytdAWSCosts?.value || '$0.00'}
            subtitle="Year to date AWS costs"
            change={parseTrend(enhancedAWSCosts?.ytdAWSCosts?.change)}
            trend={parseTrend(enhancedAWSCosts?.ytdAWSCosts?.change) > 0 ? 'up' : parseTrend(enhancedAWSCosts?.ytdAWSCosts?.change) < 0 ? 'down' : 'flat'}
            icon="cloud"
            color="costs"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Row 5: Gross Margin */}
      <Typography variant="h3" sx={{ fontWeight: 600, mb: spacing[4], display: 'flex', alignItems: 'center', gap: spacing[2] }}>
        <TrendingUpIcon color="primary" sx={{ fontSize: 32 }} />
        Gross Margin
      </Typography>
      <Grid container spacing={3} sx={{ mb: spacing[6] }} alignItems="stretch">
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title={grossMargins?.mtdMargin?.label || "MTD Margin"}
            value={grossMargins?.mtdMargin?.value || '0%'}
            subtitle={grossMargins?.mtdMargin?.subtitle || 'Month to date margin'}
            change={parseTrend(grossMargins?.mtdMargin?.change)}
            trend={parseTrend(grossMargins?.mtdMargin?.change) > 0 ? 'up' : parseTrend(grossMargins?.mtdMargin?.change) < 0 ? 'down' : 'flat'}
            icon="trending_up"
            color="revenue"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title={grossMargins?.lastMonthMargin?.label || "Last Month Margin"}
            value={grossMargins?.lastMonthMargin?.value || '0%'}
            subtitle={grossMargins?.lastMonthMargin?.subtitle || 'Previous month margin'}
            change={parseTrend(grossMargins?.lastMonthMargin?.change)}
            trend={parseTrend(grossMargins?.lastMonthMargin?.change) > 0 ? 'up' : parseTrend(grossMargins?.lastMonthMargin?.change) < 0 ? 'down' : 'flat'}
            icon="analytics"
            color="revenue"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title={grossMargins?.ytdMargin?.label || "YTD Margin"}
            value={grossMargins?.ytdMargin?.value || '0%'}
            subtitle={grossMargins?.ytdMargin?.subtitle || 'Year to date margin'}
            change={parseTrend(grossMargins?.ytdMargin?.change)}
            trend={parseTrend(grossMargins?.ytdMargin?.change) > 0 ? 'up' : parseTrend(grossMargins?.ytdMargin?.change) < 0 ? 'down' : 'flat'}
            icon="trending_up"
            color="revenue"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
          <MetricCard
            title={grossMargins?.projectedAnnualMargin?.label || "Projected Annual Margin"}
            value={grossMargins?.projectedAnnualMargin?.value || '0%'}
            subtitle={grossMargins?.projectedAnnualMargin?.subtitle || 'Projected full year margin'}
            change={parseTrend(grossMargins?.projectedAnnualMargin?.change)}
            trend={parseTrend(grossMargins?.projectedAnnualMargin?.change) > 0 ? 'up' : parseTrend(grossMargins?.projectedAnnualMargin?.change) < 0 ? 'down' : 'flat'}
            icon="schedule"
            color="revenue"
            loading={loading}
          />
        </Grid>
      </Grid>

      <DataSourceAttribution
        explainer="Revenue is calculated from Stripe payment events recorded in revenue_transactions. Subscription counts come from users.subscription_status joined to subscription_plan_map."
        source="revenueRepository + subscriptionRepository"
        endpoint="/api/dashboard/overview"
        reportingCutoff="2026-05-01T00:00:00Z"
      />
    </Box>
  );
};

export default DashboardOverview;